import { toJS } from 'mobx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getBlackbaudAuthorizeUrl, getBlackbaudRedirectUri } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';

import { ADD_APPLICATION_STEP_KEY, CONNECT_STEP_KEY } from './raisersEdgeConfig';

export interface IRaisersEdgeSetup {
  /** Steps done: 1 once the customer confirmed the marketplace step, else 0. */
  completedCount: number;
  /** The step whose action is in progress. Connect keeps it set through the redirect. */
  loadingKey: string | undefined;
  /** The setup CTA handler. It resolves when a request fails, so the kit's Button adds no second toast. */
  handleAction: (key: string) => Promise<void>;
}

/**
 * The pre-connect setup flow for the v2 page: confirm the marketplace step, then create the
 * integration record if there is none and send the customer to Blackbaud OAuth.
 * The caller must be an `observer`, because `completedCount` reads the account setting.
 */
export function useRaisersEdgeSetup(): IRaisersEdgeSetup {
  const { t } = useTranslation();
  const account = useAccount();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();
  const [loadingKey, setLoadingKey] = useState<string>();
  // State updates are async, so a fast second click still sees no loadingKey.
  const isConnectingRef = useRef(false);

  useEffect(() => {
    // The browser can restore this page from its back/forward cache after the OAuth redirect.
    // The restored page must not keep the Connect spinner and the guard.
    function handlePageShow(event: PageTransitionEvent): void {
      if (event.persisted) {
        isConnectingRef.current = false;
        setLoadingKey(undefined);
      }
    }

    globalThis.addEventListener('pageshow', handlePageShow);
    return (): void => {
      globalThis.removeEventListener('pageshow', handlePageShow);
    };
    // The listener reads only a ref and a state setter, so it never needs to re-register.
  }, []);

  const completedCount = account.getSetting('confirmed_bbrenxt_marketplace_sync') ? 1 : 0;

  async function confirmMarketplace(): Promise<void> {
    // Send only the settings. patchDirty() would also send other unsaved account edits.
    // The step completes from the saved response, so Connect stays locked until the save lands.
    const settings = { ...toJS(account.get('settings')), confirmed_bbrenxt_marketplace_sync: true };
    await account.patch({ settings });
    // A rachis patch resolves when the request fails (an HTTP error or a network error) and sets isErrored.
    if (account.isErrored) {
      toast(t("Couldn't save confirmation. Try again."), { type: 'error' });
    }
  }

  async function connect(): Promise<void> {
    if (isConnectingRef.current) {
      return;
    }
    isConnectingRef.current = true;
    setLoadingKey(CONNECT_STEP_KEY);

    try {
      await BlackbaudRaisersEdgeIntegrations.findOrCreate(
        getBlackbaudRedirectUri(globalThis.location.origin),
      );
      // Keep loadingKey set: the spinner must stay until the browser leaves the page.
      globalThis.location.assign(
        getBlackbaudAuthorizeUrl(globalThis.location.origin, globalThis.location.pathname),
      );
    } catch {
      isConnectingRef.current = false;
      setLoadingKey(undefined);
      toast(t("Couldn't connect to Blackbaud. Try again."), { type: 'error' });
    }
  }

  async function handleAction(key: string): Promise<void> {
    if (key === ADD_APPLICATION_STEP_KEY) {
      await confirmMarketplace();
    } else if (key === CONNECT_STEP_KEY) {
      await connect();
    }
  }

  return { completedCount, handleAction, loadingKey };
}
